import { cva, type VariantProps } from 'class-variance-authority';

/**
 * StickyBar variants.
 *
 * A docked bar is fixed to a screen edge at every screen size; pick the
 * sizes it appears on with the block's visibility settings. A bar that
 * waits for a scroll point sits just past its edge, hidden, slides in once
 * the named block scrolls away, and slides back out when the block
 * returns. When the named block can't be found it stays in normal flow.
 *
 * `invisible` (not `hidden`) is what keeps the waiting bar out of the
 * accessibility tree and tab order, because `display: none` can't animate.
 *
 * A docked bar is capped at the screen height and scrolls on its own, so
 * tall content or browser zoom can't cover the whole page. At the bottom
 * edge it pads by the device safe area so content clears the home
 * indicator on phones.
 *
 * A docked bar sits on the `z-sticky` layer: above page content, below the
 * `z-50` page chrome (Navbar and its panels, ComparisonBar) and overlays.
 */
export const stickyBarVariants = cva('w-full min-w-0', {
  variants: {
    state: {
      hidden:
        'fixed inset-x-0 z-sticky max-h-dvh overflow-y-auto invisible pointer-events-none opacity-0',
      shown:
        'fixed inset-x-0 z-sticky max-h-dvh overflow-y-auto visible opacity-100',
      inFlow: '',
    },
    screenEdge: {
      top: '',
      bottom: '',
    },
    spaceAbove: {
      none: '',
      header: '',
    },
    shadow: {
      none: '',
      sm: '',
      md: '',
      lg: '',
    },
  },
  compoundVariants: [
    {
      state: ['hidden', 'shown'],
      class:
        'transition-[transform,opacity,visibility] duration-normal motion-reduce:transition-none',
    },
    { state: 'shown', class: 'translate-y-0' },
    { state: 'hidden', screenEdge: 'top', class: '-translate-y-full' },
    { state: 'hidden', screenEdge: 'bottom', class: 'translate-y-full' },
    {
      state: ['hidden', 'shown'],
      screenEdge: 'top',
      spaceAbove: 'none',
      class: 'top-0',
    },
    {
      state: ['hidden', 'shown'],
      screenEdge: 'top',
      spaceAbove: 'header',
      class: 'top-24 max-h-[calc(100dvh-theme(spacing.24))]',
    },
    {
      state: ['hidden', 'shown'],
      screenEdge: 'bottom',
      class: 'bottom-0 pb-[env(safe-area-inset-bottom)]',
    },
    { state: ['hidden', 'shown'], shadow: 'sm', class: 'shadow-sm' },
    { state: ['hidden', 'shown'], shadow: 'md', class: 'shadow-md' },
    { state: ['hidden', 'shown'], shadow: 'lg', class: 'shadow-lg' },
  ],
  defaultVariants: {
    state: 'inFlow',
    screenEdge: 'top',
    spaceAbove: 'none',
    shadow: 'none',
  },
});

export type StickyBarVariantProps = VariantProps<typeof stickyBarVariants>;
